import * as events from '../base/constant';
import { Popup } from '@syncfusion/ej2-popups';
import { RadioButton } from '@syncfusion/ej2-buttons';
import { isNullOrUndefined as isNOU, isNullOrUndefined, detach, extend, addClass, removeClass, getComponent, createElement } from '@syncfusion/ej2-base';
import { getUniqueID, Browser, closest } from '@syncfusion/ej2-base';
import { CLS_RTE_PASTE_KEEP_FORMAT, CLS_RTE_PASTE_REMOVE_FORMAT, CLS_RTE_PASTE_PLAIN_FORMAT } from '../base/classes';
import { CLS_RTE_PASTE_OK, CLS_RTE_PASTE_CANCEL, CLS_RTE_DIALOG_MIN_HEIGHT } from '../base/classes';
import { NodeSelection } from '../../selection/selection';
import * as EVENTS from './../../common/constant';
import { RenderType } from '../base/enum';
import { ImageInputSource } from '../../common/enum';
import { Uploader } from '@syncfusion/ej2-inputs';
import * as classes from '../base/classes';
import { sanitizeHelper } from '../base/util';
import { cleanHTMLString, getStructuredHtml, scrollToCursor } from '../../common/util';
import { PasteCleanupAction } from '../../editor-manager/plugin/paste-clean-up-action';
import { PASTE_SOURCE } from './../../editor-manager/base';
/**
 * PasteCleanup module called when pasting content in RichTextEditor
 */
export class PasteCleanup {
    constructor(parent, serviceLocator) {
        this.isNotFromHtml = false;
        this.containsHtml = false;
        this.cropImageData = [];
        this.plainTextContent = '';
        this.pendingPasteQueue = [];
        this.validFiles = [];
        this.parent = parent;
        this.locator = serviceLocator;
        this.renderFactory = this.locator.getService('rendererFactory');
        this.i10n = serviceLocator.getService('rteLocale');
        this.dialogRenderObj = serviceLocator.getService('dialogRenderObject');
        this.addEventListener();
        this.isDestroyed = false;
    }
    addEventListener() {
        this.nodeSelectionObj = new NodeSelection(this.parent.inputElement);
        if (this.parent.isDestroyed) {
            return;
        }
        this.parent.on(events.pasteClean, this.pasteClean, this);
        this.parent.on(events.bindOnEnd, this.bindOnEnd, this);
        this.parent.on(events.bindCssClass, this.setCssClass, this);
        this.parent.on(events.destroy, this.destroy, this);
        this.parent.on(events.docClick, this.docClick, this);
        this.parent.on(events.updateProperty, this.updatePasteCleanupProperty, this);
    }
    destroy() {
        if (this.isDestroyed) {
            return;
        }
        if (this.fireFoxUploadTime) {
            clearTimeout(this.fireFoxUploadTime);
            this.fireFoxUploadTime = null;
        }
        if (this.refreshPopupTime) {
            clearTimeout(this.refreshPopupTime);
            this.refreshPopupTime = null;
        }
        if (this.failureTime) {
            clearTimeout(this.failureTime);
            this.failureTime = null;
        }
        this.removeEventListener();
        const mediaPopups = this.parent.element.querySelectorAll('.e-rte-upload-popup');
        for (let i = 0; i < mediaPopups.length; i++) {
            const uploader = this.getUploaderInstance(mediaPopups[i]);
            if (uploader && !uploader.isDestroyed) {
                uploader.destroy();
            }
            const popup = getComponent(mediaPopups[i], 'popup');
            if (popup && !popup.isDestroyed) {
                popup.destroy();
            }
        }
        if (this.keepRadioButton && !this.keepRadioButton.isDestroyed) {
            this.keepRadioButton.destroy();
            this.keepRadioButton = null;
        }
        if (this.cleanRadioButton && !this.cleanRadioButton.isDestroyed) {
            this.cleanRadioButton.destroy();
            this.cleanRadioButton = null;
        }
        if (this.plainTextRadioButton && !this.plainTextRadioButton.isDestroyed) {
            this.plainTextRadioButton.destroy();
            this.plainTextRadioButton = null;
        }
        this.isDestroyed = true;
        this.plainTextContent = '';
        this.pendingPasteQueue = [];
        this.validFiles = [];
    }
    removeEventListener() {
        this.parent.off(events.pasteClean, this.pasteClean);
        this.parent.off(events.bindCssClass, this.setCssClass);
        this.parent.off(events.destroy, this.destroy);
        this.parent.off(events.docClick, this.docClick);
        this.parent.off(events.bindOnEnd, this.bindOnEnd);
        this.parent.off(events.updateProperty, this.updatePasteCleanupProperty);
    }
    /*
     * Initializes the PasteCleanupAction  object in the editor manager after editor initialization is complete.
     * This method binds the paste cleanup module to the editor's formatter for handling paste cleanup related operations.
     */
    bindOnEnd() {
        if (this.parent.editorMode === 'HTML' && this.parent.formatter && this.parent.formatter.editorManager
            && this.parent.contentModule) {
            const pasteModel = this.getPasteCleanupModel();
            this.parent.formatter.editorManager.pasteObj = this.pasteObj =
                new PasteCleanupAction(this.parent.formatter.editorManager, pasteModel);
        }
    }
    /* Creates and returns a paste cleanup model with editor configuration and callback methods */
    getPasteCleanupModel() {
        // Create TableCommand with table model containing required methods
        const pasteModel = {
            rteElement: this.parent.element,
            enterKey: this.parent.enterKey,
            rootContainer: this.parent.rootContainer,
            enableXhtml: this.parent.enableXhtml,
            iframeSettings: this.parent.iframeSettings,
            pasteCleanupSettings: this.parent.pasteCleanupSettings,
            insertImageSettings: this.parent.insertImageSettings,
            // Retrieves the maximum allowed width for an image within the editor.
            getInsertImgMaxWidth: () => {
                return this.parent.getInsertImgMaxWidth();
            },
            // Method for retrieving the document object of the content module
            getDocument: () => {
                if (!this.contentModule) {
                    return this.parent.contentModule.getDocument();
                }
                return this.contentModule.getDocument();
            },
            // Method for retrieving the editable element object of the content module
            getEditPanel: () => {
                if (!this.contentModule) {
                    return this.parent.contentModule.getEditPanel();
                }
                return this.contentModule.getEditPanel();
            },
            updateValue: () => {
                this.parent.updateValue();
            },
            imageUpload: () => {
                this.imgUploading(this.parent.inputElement);
            },
            getCropImageData: () => {
                return this.getCropImageData();
            }
        };
        return pasteModel;
    }
    /* Updates the paste cleanup object with the latest editor configuration settings */
    updatePasteCleanupProperty() {
        const pasteModel = this.getPasteCleanupModel();
        if (!isNullOrUndefined(this.pasteObj)) {
            this.pasteObj.updatePasteCleanupModel(pasteModel);
        }
    }
    /**
     * Handles the paste cleanup operation when content is pasted into the editor
     *
     * @param {NotifyArgs} e - The notification arguments containing event data
     * @returns {void}
     */
    pasteClean(e) {
        const args = {
            requestType: 'Paste',
            editorMode: this.parent.editorMode,
            event: e
        };
        let value = null;
        let imageproperties;
        const allowedTypes = this.parent.insertImageSettings.allowedTypes;
        // Extract clipboard data if available
        if (e.args && !isNOU(e.args.clipboardData)) {
            value = e.args.clipboardData.getData('text/html');
            // Store plain text content for later use
            if (e.args.clipboardData.getData('text/plain')) {
                this.plainTextContent = e.args.clipboardData.getData('text/plain');
            }
        }
        // Process only if we have args, value, and in HTML mode
        if (e.args && value !== null && this.parent.editorMode === 'HTML') {
            const shouldContinue = this.processHtmlPaste(e, value, args, allowedTypes, imageproperties);
            if (!shouldContinue) {
                return; // Stop processing if indicated
            }
        }
    }
    /* Processes HTML content from paste operation */
    processHtmlPaste(e, value, args, allowedTypes, imageproperties) {
        let processedValue = value;
        let shouldContinue = true;
        const files = e.args.clipboardData.files;
        const elm = createElement('p');
        elm.innerHTML = value;
        const source = this.findSource(elm);
        // Handle empty HTML content (plain text or image)
        if (source === 'html' && !isNOU(files) && files.length > 0) {
            const result = this.handleFileDataPaste(e, value, args, allowedTypes, imageproperties);
            processedValue = result.value;
            shouldContinue = result.shouldContinue;
            if (!shouldContinue) {
                return false; // Stop processing
            }
        }
        else if (value.length > 0) { // Handle non-empty HTML content
            processedValue = this.handleHtmlValuePaste(e, value, args);
        }
        else if (value.length === 0) { // handle for the plain text case
            const result = this.handlePlainTextPaste(e, value, args);
            processedValue = result.value;
            shouldContinue = result.shouldContinue;
            if (!shouldContinue) {
                return false;
            }
        }
        processedValue = this.handleGoogleDocs(processedValue);
        // Remove base tags from content
        if (processedValue !== null && processedValue !== '') {
            processedValue = processedValue.replace(/<base[^>]*>/g, '');
        }
        this.prepareAndInsertContent(e, processedValue, args);
        return true;
    }
    // when google docs has b tags wrapped in default need to remove it
    handleGoogleDocs(processedValue) {
        // check if the content is pasted from google docs
        if (processedValue && processedValue.indexOf('id="docs-internal-guid') >= 0) {
            // remove the docs-internal-guid id attribute entirely
            processedValue = processedValue.replace(/\s*id="docs-internal-guid[^"]*"/gi, '');
            // check inside the b tags and do manipulation
            processedValue = processedValue.replace(/<b\b([^>]*)>([\s\S]*?)<\/b>/gi, (match, attrs, inner) => {
                // Look inside the inner HTML for a span with font-weight:700
                const hasBoldSpan = /<span[^>]*style=["'][^"']*font-weight\s*:\s*700[^"']*["'][^>]*>/i.test(inner);
                // Look inside the inner HTML for a span with font-style:style
                const hasItalicSpan = /<span[^>]*style=["'][^"']*font-style\s*:\s*italic[^"']*["'][^>]*>/i.test(inner);
                if (hasBoldSpan && hasItalicSpan) {
                    // Wrap with both <b> and <em>
                    return `<b><em>${inner}</em></b>`;
                }
                if (hasBoldSpan && !hasItalicSpan) {
                    // keep <b> if the nested span is bold but not italic
                    return `<b>${inner}</b>`;
                }
                if (hasItalicSpan) {
                    // convert to <em> if the nested span is italic
                    return `<em>${inner}</em>`;
                }
                // otherwise unwrap <b>
                return inner;
            });
        }
        return processedValue;
    }
    findSource(element) {
        const metaNodes = element.querySelectorAll('meta');
        for (let i = 0; i < metaNodes.length; i++) {
            const metaNode = metaNodes[i];
            const content = metaNode.getAttribute('content');
            const name = metaNode.getAttribute('name');
            if (name && name.toLowerCase().indexOf('generator') >= 0 && content && content.toLowerCase().indexOf('microsoft') >= 0) {
                for (let j = 0; j < PASTE_SOURCE.length; j++) {
                    if (content.toLowerCase().indexOf(PASTE_SOURCE[j]) >= 0) {
                        return PASTE_SOURCE[j];
                    }
                }
            }
        }
        // Fallback: Check for Excel-specific patterns in HTML structure
        if (this.isExcelContent(element) && (this.parent.userAgentData.getBrowser() === 'Safari')) {
            return 'excel';
        }
        return 'html';
    }
    /* Detects Excel content by checking for Excel-specific HTML patterns */
    isExcelContent(element) {
        // Check for Excel-specific table attributes and styles
        const tables = element.querySelectorAll('table');
        if (tables.length > 0) {
            const table = tables[0];
            // Check for Excel-specific style patterns
            const style = (table.getAttribute('style') || '').toLowerCase();
            if (style.indexOf('border-collapse') >= 0 || style.indexOf('mso-') >= 0) {
                return true;
            }
        }
        return false;
    }
    /* Handles plain text paste with event notification and callback */
    handlePlainTextPaste(e, value, args) {
        let processedValue = value || e.args.clipboardData.getData('text/plain');
        const htmlRegex = new RegExp(/<\/[a-z][\s\S]*>/i);
        // Trigger beforePasteCleanup event
        this.parent.trigger(events.beforePasteCleanup, { value: processedValue }, (updatedArgs) => { processedValue = updatedArgs.value; });
        // Escape HTML characters
        processedValue = processedValue.replace(/</g, '&lt;');
        processedValue = processedValue.replace(/>/g, '&gt;');
        // Mark as plain text content
        this.plainTextContent = processedValue;
        // Notify paste event with callback for custom handling
        this.parent.notify(events.paste, {
            file: null,
            args: e.args,
            text: processedValue,
            callBack: (b) => {
                if (typeof (b) === 'string') {
                    processedValue = b;
                }
            }
        });
        // Format plain text with line breaks
        if (!htmlRegex.test(processedValue)) {
            const divElement = this.parent.createElement('div');
            divElement.innerHTML = this.pasteObj.splitBreakLine(processedValue);
            processedValue = divElement.innerHTML;
        }
        return { value: processedValue, shouldContinue: true };
    }
    /* Handles case when HTML value is empty but may contain plain text or images */
    handleFileDataPaste(e, value, args, allowedTypes, imageproperties) {
        const htmlRegex = new RegExp(/<\/[a-z][\s\S]*>/i);
        let processedValue = e.args.clipboardData.getData('text/plain');
        this.parent.trigger(events.beforePasteCleanup, { value: processedValue }, (updatedArgs) => { processedValue = updatedArgs.value; });
        this.isNotFromHtml = processedValue !== '' ? true : false;
        processedValue = processedValue.replace(/</g, '&lt;');
        processedValue = processedValue.replace(/>/g, '&gt;');
        this.containsHtml = htmlRegex.test(processedValue);
        this.plainTextContent = processedValue;
        // Collect clipboard files
        const clipboardData = e.args.clipboardData;
        const clipboardFiles = clipboardData && clipboardData.files && clipboardData.files.length ?
            Array.from(clipboardData.files) : [];
        // Filter valid image files
        const validImageFiles = [];
        for (const file of clipboardFiles) {
            const fileName = file.name || '';
            const imgType = fileName.substring(fileName.lastIndexOf('.'));
            if (this.isInvalidImageType(imgType, allowedTypes)) {
                continue;
            }
            validImageFiles.push(file);
        }
        this.validFiles = validImageFiles;
        // Process the image pasting with validImageFiles
        const result = this.processPasteWithImage(e, validImageFiles, processedValue, args, imageproperties, htmlRegex);
        return result;
    }
    /* Checks if image type is not in allowed types */
    isInvalidImageType(imgType, allowedTypes) {
        return allowedTypes.every((type) => type.toLowerCase() !== imgType);
    }
    /* Processes paste operation that may contain an image */
    processPasteWithImage(e, file, value, args, imageproperties, htmlRegex) {
        let processedValue = value;
        //let shouldContinue: boolean = true;
        this.parent.notify(events.paste, {
            file: file,
            args: e.args,
            text: processedValue,
            callBack: (b) => {
                imageproperties = b;
                if (typeof (imageproperties) === 'object') {
                    this.parent.formatter.editorManager.execCommand('Images', 'Image', e.args, this.imageFormatting.bind(this, args), 'pasteCleanup', imageproperties, 'pasteCleanupModule');
                }
                else {
                    processedValue = imageproperties;
                }
            }
        });
        // Format plain text with line breaks if not HTML
        if (!htmlRegex.test(processedValue)) {
            const divElement = this.parent.createElement('div');
            divElement.innerHTML = this.pasteObj.splitBreakLine(processedValue);
            processedValue = divElement.innerHTML;
        }
        return { value: processedValue, shouldContinue: true };
    }
    /* Handles non-empty HTML content */
    handleHtmlValuePaste(e, value, args) {
        let processedValue = value;
        this.parent.trigger(events.beforePasteCleanup, { value: processedValue }, (updatedArgs) => { processedValue = updatedArgs.value; });
        this.parent.formatter.editorManager.observer.notify(EVENTS.MS_WORD_CLEANUP, {
            args: e.args,
            text: processedValue,
            allowedStylePropertiesArray: this.parent.pasteCleanupSettings.allowedStyleProps,
            callBack: (a, cropImageData, pasteTableSource) => {
                args.pasteTableSource = pasteTableSource;
                processedValue = a.trim();
                this.cropImageData = cropImageData;
            }
        });
        return processedValue;
    }
    /* Prepares and inserts content into the editor */
    prepareAndInsertContent(e, value, args) {
        this.contentModule = this.renderFactory.getRenderer(RenderType.Content);
        const currentDocument = this.contentModule.getDocument();
        const range = this.nodeSelectionObj.getRange(currentDocument);
        this.saveSelection = this.nodeSelectionObj.save(range, currentDocument);
        // Process content through temp div
        const tempDivElem = this.parent.createElement('div');
        tempDivElem.innerHTML = value;
        // Handle unsupported images
        this.processUnsupportedImages(tempDivElem);
        const processedValue = tempDivElem.innerHTML;
        // Check if content is not empty
        const isValueNotEmpty = tempDivElem.textContent !== '' ||
            !isNOU(tempDivElem.querySelector('img')) ||
            !isNOU(tempDivElem.querySelector('table'));
        // Clean up resize elements
        let finalValue = processedValue;
        this.parent.notify(events.cleanupResizeElements, {
            value: processedValue,
            callBack: (currentValue) => {
                finalValue = currentValue;
            }
        });
        // Removes the \n from the value
        finalValue = cleanHTMLString(finalValue, this.parent.element);
        finalValue = getStructuredHtml(finalValue, this.parent.enterKey, this.parent.enableHtmlEncode, true);
        // Handle paste based on settings
        this.handlePasteBasedOnSettings(e, finalValue, args, isValueNotEmpty);
    }
    /* Processes unsupported images in pasted content */
    processUnsupportedImages(tempDivElem) {
        const unsupportedImg = tempDivElem.querySelectorAll('.e-rte-image-unsupported');
        for (let index = 0; index < unsupportedImg.length; index++) {
            unsupportedImg[index].setAttribute('alt', this.i10n.getConstant('unsupportedImage'));
            unsupportedImg[index].classList.remove('e-rte-image-unsupported');
        }
    }
    /* Handles paste based on editor settings */
    handlePasteBasedOnSettings(e, value, args, isValueNotEmpty) {
        if (this.parent.pasteCleanupSettings.prompt && !e.isPlainPaste) {
            if (isValueNotEmpty) {
                e.args.preventDefault();
                this.pasteDialog(value, args);
            }
        }
        else if (this.parent.pasteCleanupSettings.plainText) {
            e.args.preventDefault();
            this.plainFormatting(value, args);
        }
        else if (this.parent.pasteCleanupSettings.keepFormat || e.isPlainPaste) {
            e.args.preventDefault();
            this.formatting(value, false, args);
        }
        else {
            e.args.preventDefault();
            this.formatting(value, true, args);
        }
    }
    /* Processes and uploads images from pasted content */
    imgUploading(elm) {
        const allImgElm = elm.querySelectorAll('.pasteContent_Img');
        // Handle image upload if save URL is configured and images exist
        if (this.parent.insertImageSettings.saveUrl && allImgElm.length > 0) {
            this.processImagesWithSaveUrl(allImgElm);
        }
        else if (this.parent.insertImageSettings.saveFormat === 'Blob') {
            // Convert to blob format if specified
            this.pasteObj.getBlob(allImgElm);
        }
        // Clean up temporary CSS classes from images
        this.cleanupImageClasses(elm, allImgElm);
    }
    /* Processes images when save URL is configured */
    processImagesWithSaveUrl(allImgElm) {
        const base64Src = [];
        const imgName = [];
        const uploadImg = [];
        // Collect base64 images
        for (let i = 0; i < allImgElm.length; i++) {
            const imgSrc = allImgElm[i].getAttribute('src');
            if (!isNOU(imgSrc) && imgSrc.split(',')[0].indexOf('base64') >= 0) {
                base64Src.push(imgSrc);
                imgName.push(getUniqueID('rte_image'));
                uploadImg.push(allImgElm[i]);
            }
        }
        // Convert base64 to files
        const fileList = [];
        for (let i = 0; i < base64Src.length; i++) {
            fileList.push(this.pasteObj.base64ToFile(base64Src[i], imgName[i]));
        }
        // Upload each file
        for (let i = 0; i < fileList.length; i++) {
            this.uploadMethod(fileList[i], uploadImg[i]);
        }
        // Convert to blob if needed
        if (isNOU(this.parent.insertImageSettings.path) &&
            this.parent.insertImageSettings.saveFormat === 'Blob') {
            this.pasteObj.getBlob(allImgElm);
        }
    }
    /* Removes temporary CSS classes from processed images */
    cleanupImageClasses(elm, allImgElm) {
        const allImgElmId = elm.querySelectorAll('.pasteContent_Img');
        for (let i = 0; i < allImgElmId.length; i++) {
            allImgElmId[i].classList.remove('pasteContent_Img');
            // Remove class attribute if empty
            if (allImgElmId[i].getAttribute('class').trim() === '') {
                allImgElm[i].removeAttribute('class');
            }
        }
    }
    /* Enables or disables the toolbar based on state */
    toolbarEnableDisable(state) {
        if (!this.parent.inlineMode.enable && this.parent.toolbarModule && this.parent.toolbarModule.baseToolbar) {
            if (state) {
                // To disable toolbar items
                this.parent.toolbarModule.baseToolbar.toolbarObj.disable(state);
            }
            else {
                const mediaPopups = this.parent.element.querySelectorAll('.e-rte-upload-popup');
                if (mediaPopups.length === 0) {
                    //To enable Toolbar items
                    this.parent.toolbarModule.baseToolbar.toolbarObj.disable(state);
                }
            }
        }
    }
    /* Handles the upload process for an image file */
    uploadMethod(file, imgElem) {
        this.pasteObj.setImageOpacity(imgElem);
        const popupObj = this.createPopupObject(imgElem, file);
        this.createUploader(imgElem, popupObj, file);
        this.hideFileSelectWrapper(popupObj);
    }
    /* Creates and configures the popup object */
    createPopupObject(imgElem, file) {
        const popupEle = this.pasteObj.createPopupElement();
        popupEle.setAttribute('id', getUniqueID(this.parent.getID() + '_upload_popup'));
        const boundObj = { popupRoot: popupEle, self: this };
        const contentEle = this.parent.createElement('input', {
            id: getUniqueID(this.parent.getID() + '_upload'), attrs: { type: 'File', name: 'UploadFiles' }
        });
        const popup = new Popup(popupEle, {
            relateTo: imgElem,
            height: '85px',
            width: '300px',
            content: contentEle,
            viewPortElement: this.parent.inputElement,
            enableRtl: this.parent.enableRtl,
            zIndex: 10001,
            close: this.onPopupClose.bind(boundObj)
        });
        this.configurePopupStyles(popup);
        this.schedulePopupRefresh(imgElem, file, popup);
        return popup;
    }
    /* Handle uploader popup close behavior */
    onPopupClose() {
        const currentPopupElem = this.popupRoot;
        const currentPasteCleanupObj = this.self;
        if (!isNOU(currentPopupElem) && !isNOU(currentPasteCleanupObj)) {
            const popupObj = getComponent(currentPopupElem, 'popup');
            currentPasteCleanupObj.parent.isBlur = false;
            if (isNOU(popupObj)) {
                return;
            }
            const uploaderObj = currentPasteCleanupObj.getUploaderInstance(currentPopupElem);
            if (isNOU(uploaderObj)) {
                return;
            }
            uploaderObj.destroy();
            popupObj.destroy();
            detach(currentPopupElem);
        }
    }
    /* To get the uploader object from popup element */
    getUploaderInstance(element) {
        const currentUploader = element.querySelector('.e-uploader');
        return getComponent(currentUploader, 'uploader');
    }
    /* Configures popup styles and CSS classes */
    configurePopupStyles(popup) {
        if (isNOU(popup) || isNOU(popup.element)) {
            return;
        }
        popup.element.style.display = 'none';
        addClass([popup.element], [classes.CLS_POPUP_OPEN, classes.CLS_RTE_UPLOAD_POPUP]);
        if (!isNOU(this.parent.cssClass)) {
            addClass([popup.element], this.parent.cssClass.replace(/\s+/g, ' ').trim().split(' '));
        }
    }
    /* Schedules popup refresh based on file size */
    schedulePopupRefresh(imgElem, file, popupObj) {
        const timeOut = file.size > 1000000 ? 300 : 100;
        this.refreshPopupTime = setTimeout(() => {
            this.refreshPopup(imgElem, popupObj);
        }, timeOut);
    }
    /* Creates and configures the uploader component */
    createUploader(imgElem, popup, file) {
        const uploadObj = new Uploader({
            asyncSettings: {
                saveUrl: this.parent.insertImageSettings.saveUrl,
                removeUrl: this.parent.insertImageSettings.removeUrl
            },
            cssClass: classes.CLS_RTE_DIALOG_UPLOAD,
            allowedExtensions: this.parent.insertImageSettings.allowedTypes.toString(),
            success: (e) => {
                this.popupClose(popup, imgElem, e);
            },
            uploading: (e) => this.handleUploading(e, imgElem, popup),
            beforeUpload: (args) => this.handleBeforeUpload(args),
            failure: (e) => this.handleFailure(e, imgElem, popup),
            canceling: () => this.handleCanceling(imgElem, popup),
            selected: (e) => { e.cancel = true; },
            removing: () => this.handleRemoving(imgElem, popup)
        });
        uploadObj.appendTo(popup.element.childNodes[0]);
        this.initializeUpload(file, uploadObj);
        return uploadObj;
    }
    /* Handles the uploading event */
    handleUploading(e, imgElem, popupObj) {
        if (!this.parent.isServerRendered) {
            this.parent.trigger(events.imageUploading, e, (imageUploadingArgs) => {
                if (imageUploadingArgs.cancel) {
                    if (!isNullOrUndefined(imgElem)) {
                        detach(imgElem);
                    }
                    if (!isNullOrUndefined(popupObj.element)) {
                        detach(popupObj.element);
                    }
                }
            });
        }
    }
    /* Handles the beforeUpload event */
    handleBeforeUpload(args) {
        this.parent.trigger(events.beforeImageUpload, args);
        this.toolbarEnableDisable(true);
    }
    /* Handles upload failure */
    handleFailure(e, imgElem, popupObj) {
        this.failureTime = setTimeout(() => {
            this.uploadFailure(imgElem, popupObj, e);
        }, 900);
    }
    /* Handles upload cancellation */
    handleCanceling(imgElem, popupObj) {
        if (imgElem.nextSibling && imgElem.nextSibling.textContent === ' ') {
            detach(imgElem.nextSibling);
        }
        detach(imgElem);
        popupObj.close();
    }
    /* Handles file removal */
    handleRemoving(imgElem, popupObj) {
        if (imgElem.nextSibling && imgElem.nextSibling.textContent === ' ') {
            detach(imgElem.nextSibling);
        }
        detach(imgElem);
        popupObj.close();
    }
    /* Initializes the upload process */
    initializeUpload(file, uploadObj) {
        const fileInfo = [{
                name: file.name,
                rawFile: file,
                size: file.size,
                type: file.type,
                status: 'Ready to Upload',
                validationMessages: { minSize: '', maxSize: '' },
                statusCode: '1'
            }];
        uploadObj.createFileList(fileInfo);
        uploadObj.upload(fileInfo);
    }
    /* Hides the file select wrapper */
    hideFileSelectWrapper(popupObj) {
        if (isNOU(popupObj) || isNOU(popupObj.element)) {
            return;
        }
        const fileSelectWrap = popupObj.element.getElementsByClassName('e-file-select-wrap')[0];
        if (fileSelectWrap) {
            fileSelectWrap.style.display = 'none';
        }
    }
    /* Handles image upload failure */
    uploadFailure(imgElem, popupObj, e) {
        if ((this.parent && this.parent.isDestroyed) || isNOU(popupObj)) {
            return;
        }
        detach(imgElem);
        popupObj.close();
        this.parent.trigger(events.imageUploadFailed, e);
    }
    /* Handles popup close after successful upload */
    popupClose(popupObj, imgElem, e) {
        this.prepareEventArgs(e, imgElem);
        this.handleUploadStatus(e, imgElem);
        this.scheduleCleanup(popupObj, imgElem);
    }
    /* Prepares event arguments for upload events */
    prepareEventArgs(e, imgElem) {
        e.element = imgElem;
        e.detectImageSource = ImageInputSource.Pasted;
    }
    /* Handles different upload status codes */
    handleUploadStatus(e, imgElem) {
        const element = e.file;
        if (isNOU(element)) {
            return;
        }
        if (element.statusCode === '2') {
            this.handleSuccessfulUpload(e, imgElem);
        }
        else if (element.statusCode === '5') {
            this.handleImageRemoval(e);
        }
    }
    /* Handles successful image upload */
    handleSuccessfulUpload(e, imgElem) {
        this.parent.trigger(events.imageUploadSuccess, e, (e) => {
            if (!isNullOrUndefined(this.parent.insertImageSettings.path)) {
                const url = this.parent.insertImageSettings.path + e.file.name;
                this.updateImageSource(imgElem, url, e.file.name);
            }
        });
    }
    /* Updates image source after successful upload */
    updateImageSource(imgElem, url, altText) {
        // Handle detached images with ID
        if (!this.parent.inputElement.contains(imgElem) && imgElem.id) {
            const imgHtmlElems = this.parent.inputElement.querySelectorAll('#' + imgElem.id);
            this.updateDetachedImages(imgHtmlElems, url, altText);
        }
        else {
            imgElem.src = url;
            imgElem.setAttribute('alt', altText);
        }
    }
    /* Updates detached images by ID */
    updateDetachedImages(imgHtmlElems, url, altText) {
        for (let i = 0; i < imgHtmlElems.length; i++) {
            const imgHtmlElem = imgHtmlElems[i];
            if (imgHtmlElem && imgHtmlElem.style && imgHtmlElem.style.opacity === '0.5') {
                imgHtmlElem.src = url;
                imgHtmlElem.setAttribute('alt', altText);
            }
        }
    }
    /* Handles image removal */
    handleImageRemoval(e) {
        this.parent.trigger(events.imageRemoving, e, (e) => {
            if (!isNullOrUndefined(e.element.src)) {
                e.element.src = '';
            }
        });
    }
    /* Schedules cleanup after upload completion */
    scheduleCleanup(popupObj, imgElem) {
        popupObj.close();
        this.resetImageOpacity(imgElem);
        this.toolbarEnableDisable(false);
    }
    /* Resets image opacity after upload */
    resetImageOpacity(imgElem) {
        if (!this.parent.inputElement.contains(imgElem) && (imgElem.id || imgElem.alt)) {
            this.resetDetachedImageOpacity(imgElem);
        }
        else {
            imgElem.style.opacity = '1';
        }
    }
    /* Resets opacity for detached images */
    resetDetachedImageOpacity(imgElem) {
        const selector = imgElem.id ?
            `#${imgElem.id}` :
            `[alt="${imgElem.alt}"]`;
        if (selector) {
            const imgHtmlElems = this.parent.inputElement.querySelectorAll(selector);
            for (let i = 0; i < imgHtmlElems.length; i++) {
                const imgHtmlElem = imgHtmlElems[i];
                if (imgHtmlElem && imgHtmlElem.style && imgHtmlElem.style.opacity === '0.5') {
                    imgHtmlElem.style.opacity = '1';
                }
            }
        }
    }
    /* Refreshes popup position relative to image */
    refreshPopup(imageElement, popupObj) {
        const imgPosition = this.calculateImagePosition(imageElement);
        const rtePosition = this.parent.element.offsetTop + this.parent.element.offsetHeight;
        if (imgPosition > rtePosition) {
            this.positionPopupAtTop(popupObj);
        }
        else {
            this.positionPopupAtImage(popupObj, imageElement);
        }
    }
    /* Calculates image position considering iframe */
    calculateImagePosition(imageElement) {
        return this.parent.iframeSettings.enable ?
            this.parent.element.offsetTop + imageElement.offsetTop :
            imageElement.offsetTop;
    }
    /* Positions popup at the top of editor */
    positionPopupAtTop(popupObj) {
        popupObj.offsetY = this.parent.iframeSettings.enable ? -30 : -65;
        popupObj.element.style.display = 'block';
    }
    /* Positions popup relative to image */
    positionPopupAtImage(popupObj, imageElement) {
        if (popupObj) {
            popupObj.refreshPosition(imageElement);
            popupObj.element.style.display = 'block';
        }
    }
    /* Method for image formatting when pasting */
    imageFormatting(pasteArgs, imgElement) {
        const imgElem = imgElement.elements[0];
        if (!isNOU(imgElem.getAttribute('src'))) {
            imgElem.classList.add('pasteContent_Img');
        }
        const imageElement = this.parent.createElement('span');
        imageElement.appendChild(imgElem);
        const imageValue = imageElement.innerHTML;
        this.contentModule = this.renderFactory.getRenderer(RenderType.Content);
        const currentDocument = this.contentModule.getDocument();
        const range = this.nodeSelectionObj.getRange(currentDocument);
        this.saveSelection = this.nodeSelectionObj.save(range, currentDocument);
        const settings = this.parent.pasteCleanupSettings;
        // queue this image and open prompt
        this.pendingPasteQueue.push(imageValue);
        const isImageQueueCompleted = (this.validFiles.length === 0) || (this.pendingPasteQueue.length === this.validFiles.length);
        if (!isImageQueueCompleted) {
            return;
        }
        const combinedHtml = this.pendingPasteQueue.join('');
        if (settings.prompt) {
            this.pasteDialog(combinedHtml, pasteArgs);
            return;
        }
        else if (settings.plainText) {
            this.plainFormatting(combinedHtml, pasteArgs);
            this.pendingPasteQueue = [];
        }
        else if (settings.keepFormat) {
            this.formatting(combinedHtml, false, pasteArgs);
            this.pendingPasteQueue = [];
        }
        else {
            this.formatting(combinedHtml, true, pasteArgs);
            this.pendingPasteQueue = [];
        }
    }
    /* Renders radio buttons for paste formatting options. */
    radioRender() {
        this.keepRadioButton = new RadioButton({ label: this.i10n.getConstant('keepFormat'),
            name: 'pasteOption', checked: true });
        this.keepRadioButton.isStringTemplate = true;
        const keepFormatElement = this.parent.element.querySelector('#keepFormating');
        this.keepRadioButton.appendTo(keepFormatElement);
        this.cleanRadioButton = new RadioButton({ label: this.i10n.getConstant('cleanFormat'), name: 'pasteOption' });
        this.cleanRadioButton.isStringTemplate = true;
        const cleanFormatElement = this.parent.element.querySelector('#cleanFormat');
        this.cleanRadioButton.appendTo(cleanFormatElement);
        this.plainTextRadioButton = new RadioButton({ label: this.i10n.getConstant('plainText'), name: 'pasteOption' });
        this.plainTextRadioButton.isStringTemplate = true;
        const plainTextElement = this.parent.element.querySelector('#plainTextFormat');
        this.plainTextRadioButton.appendTo(plainTextElement);
    }
    selectFormatting(value, args, keepChecked, cleanChecked) {
        if (keepChecked) {
            this.formatting(value, false, args);
        }
        else if (cleanChecked) {
            this.formatting(value, true, args);
        }
        else {
            this.plainFormatting(value, args);
        }
    }
    /* Shows the dialog for paste formatting options. */
    pasteDialog(value, args) {
        this.dialogObj = this.dialogRenderObj.render(this.getDialogModel(value, args));
        let rteDialogWrapper = this.parent.element.querySelector('#' + this.parent.getID() + '_pasteCleanupDialog');
        if (rteDialogWrapper && rteDialogWrapper.innerHTML !== '') {
            this.destroyDialog(rteDialogWrapper);
        }
        if (!rteDialogWrapper) {
            rteDialogWrapper = this.parent.createElement('div', {
                id: this.parent.getID() + '_pasteCleanupDialog'
            });
            this.parent.rootContainer.appendChild(rteDialogWrapper);
        }
        this.dialogObj.appendTo(rteDialogWrapper);
        this.radioRender();
        this.dialogObj.show();
        this.setCssClass({ cssClass: this.parent.getCssClass() });
    }
    /* Returns the dialog model configuration. */
    getDialogModel(value, args) {
        return {
            buttons: [
                this.getOkButton(value, args),
                this.getCancelButton()
            ],
            header: this.i10n.getConstant('pasteFormat'),
            content: this.getDialogContent(),
            target: this.parent.element,
            width: '300px',
            height: '265px',
            cssClass: CLS_RTE_DIALOG_MIN_HEIGHT,
            isModal: Browser.isDevice,
            visible: false
        };
    }
    /* Creates the OK button configuration for the dialog. */
    getOkButton(value, args) {
        return {
            click: () => {
                if (!this.dialogObj.isDestroyed) {
                    const keepChecked = !!this.parent.element.querySelector('#keepFormating').checked;
                    const cleanChecked = !!this.parent.element.querySelector('#cleanFormat').checked;
                    this.closeDialog();
                    this.selectFormatting(value, args, keepChecked, cleanChecked);
                }
            },
            buttonModel: {
                isPrimary: true,
                cssClass: 'e-flat ' + CLS_RTE_PASTE_OK,
                content: this.i10n.getConstant('pasteDialogOk')
            }
        };
    }
    /* Creates the Cancel button configuration for the dialog. */
    getCancelButton() {
        return {
            click: () => {
                if (!this.dialogObj.isDestroyed) {
                    this.closeDialog();
                }
            },
            buttonModel: {
                cssClass: 'e-flat ' + CLS_RTE_PASTE_CANCEL,
                content: this.i10n.getConstant('pasteDialogCancel')
            }
        };
    }
    /* Closes and destroys the dialog. */
    closeDialog() {
        this.dialogObj.hide();
        const dialogRef = this.dialogObj;
        this.dialogRenderObj.close(dialogRef);
        this.dialogObj.destroy();
        // Reset the image Queue
        this.pendingPasteQueue = [];
    }
    /* Returns the HTML content for the dialog. */
    getDialogContent() {
        return `${this.i10n.getConstant('pasteFormatContent')}<br/><div>
            <div class="e-rte-radio-keep-format">
                <input type="radio" class="${CLS_RTE_PASTE_KEEP_FORMAT}" id="keepFormating"/>
            </div>
            <div class="e-rte-radio-remove-format">
                <input type="radio" class="${CLS_RTE_PASTE_REMOVE_FORMAT}" id="cleanFormat"/>
            </div>
            <div class="e-rte-radio-plain-format">
                <input type="radio" class="${CLS_RTE_PASTE_PLAIN_FORMAT}" id="plainTextFormat"/>
            </div>
        </div>`;
    }
    /* Updates CSS classes on a component. */
    updateCss(currentObj, e) {
        if (currentObj && e.cssClass) {
            if (isNullOrUndefined(e.oldCssClass)) {
                currentObj.setProperties({ cssClass: (currentObj.cssClass + ' ' + e.cssClass).trim() });
            }
            else {
                currentObj.setProperties({ cssClass: (currentObj.cssClass.replace(e.oldCssClass, '').trim() + ' ' + e.cssClass).trim() });
            }
        }
    }
    // eslint-disable-next-line @typescript-eslint/tslint/config
    setCssClass(e) {
        const mediaPopups = this.parent.element.querySelectorAll('.e-rte-upload-popup');
        for (let i = 0; i < mediaPopups.length; i++) {
            const uploader = this.getUploaderInstance(mediaPopups[i]);
            if (uploader && !uploader.isDestroyed) {
                this.updateCss(uploader, e);
            }
            const popup = getComponent(mediaPopups[i], 'popup');
            if (popup && !popup.isDestroyed && e.cssClass) {
                if (e.oldCssClass) {
                    removeClass([popup.element], e.oldCssClass);
                }
                addClass([popup.element], e.cssClass);
            }
        }
        this.updateCss(this.dialogObj, e);
        this.updateCss(this.plainTextRadioButton, e);
        this.updateCss(this.cleanRadioButton, e);
        this.updateCss(this.keepRadioButton, e);
    }
    /* Removes the dialog container and its child elements. */
    destroyDialog(rteDialogWrapper) {
        const rteDialogContainer = this.parent.element.querySelector('.e-rte-dialog-minheight');
        if (rteDialogContainer) {
            detach(rteDialogContainer);
        }
        while (rteDialogWrapper.firstChild) {
            detach(rteDialogWrapper.firstChild);
        }
    }
    /* Handles document click to hide dialog if clicked outside. */
    docClick(e) {
        const target = e.args.target;
        if (target &&
            target.classList &&
            this.dialogObj &&
            !closest(target, `[id='${this.dialogObj.element.id}']`) &&
            !target.classList.contains('e-toolbar-item')) {
            this.dialogObj.hide();
        }
    }
    /* Processes and sanitizes pasted content based on cleanup settings, image handling, sanitization, and custom callbacks. */
    formatting(value, clean, args) {
        let clipBoardElem = this.parent.createElement('div', { className: 'pasteContent', styles: 'display:inline;' });
        // If plain text paste contains HTML, split lines appropriately
        if (this.isNotFromHtml && this.containsHtml) {
            value = this.pasteObj.splitBreakLine(value);
        }
        clipBoardElem.innerHTML = value;
        clipBoardElem = this.pasteObj.cleanAppleClass(clipBoardElem);
        // Remove denied tags and attributes as per settings
        clipBoardElem = this.pasteObj.cleanupDeniedTagsAndAttributes(clipBoardElem, clean);
        // Restrict allowed CSS style properties if configured
        if (this.parent.pasteCleanupSettings.allowedStyleProps !== null) {
            clipBoardElem = this.pasteObj.allowedStyle(clipBoardElem);
        }
        this.saveSelection.restore();
        let newText = clipBoardElem.innerHTML;
        if (this.parent.enableHtmlSanitizer) { // Sanitize innerHTML and ensure ampersands
            newText = clipBoardElem.innerHTML.split('&').join('&amp;');
        }
        clipBoardElem.innerHTML = sanitizeHelper(newText, this.parent);
        this.pasteObj.setImageClassAndProps(clipBoardElem);
        // Handle pasted <picture> elements if present
        if (this.pasteObj.hasPictureElement(clipBoardElem)) {
            this.pasteObj.processPictureElement(clipBoardElem);
        }
        // Process paste content, image conversion & emit callbacks if present
        if (this.pasteObj.hasContentToPaste(clipBoardElem)) {
            this.handlePastedImagesAndEvents(clipBoardElem, value, args);
        }
    }
    handlePastedImagesAndEvents(clipBoardElem, value, args) {
        // Based on the information in your clipboard, the function below is refactored for better readability.
        const tempWrapperElem = this.parent.createElement('div');
        tempWrapperElem.innerHTML = value;
        const filesData = this.pasteObj.collectBase64ImageFiles(tempWrapperElem);
        this.parent.trigger(events.afterPasteCleanup, { value: clipBoardElem.innerHTML, filesData: filesData }, (updatedArgs) => { value = updatedArgs.value; });
        clipBoardElem.innerHTML = this.parent.addAnchorAriaLabel(value);
        clipBoardElem = this.pasteObj.addTableClass(clipBoardElem, args.pasteTableSource);
        this.execPasteCommand(clipBoardElem, args);
        this.parent.notify(events.autoResize, {});
        scrollToCursor(this.parent.contentModule.getDocument(), this.parent.inputElement);
        this.pasteObj.removeTempClass();
        this.parent.notify(events.toolbarRefresh, {});
        this.pasteObj.cropImageHandler(this.parent.inputElement);
    }
    execPasteCommand(clipBoardElem, args) {
        this.parent.formatter.editorManager.execCommand('inserthtml', 'pasteCleanup', args, (returnArgs) => {
            extend(args, {
                elements: returnArgs.elements, imageElements: returnArgs.imgElem
            }, true);
            this.parent.formatter.onSuccess(this.parent, args);
        }, clipBoardElem, null, null, this.parent.enterKey);
    }
    getCropImageData() {
        return this.cropImageData;
    }
    /* Handles the pasting of plain text content into the rich text editor. */
    plainFormatting(value, args) {
        const clipBoardElem = this.parent.createElement('div', { className: 'pasteContent' });
        this.plainTextContent = this.plainTextContent.replace(/</g, '&lt;');
        this.plainTextContent = this.plainTextContent.replace(/>/g, '&gt;');
        const sanitizedContent = sanitizeHelper(this.plainTextContent, this.parent);
        const splitContent = this.pasteObj.splitBreakLine(sanitizedContent);
        clipBoardElem.innerHTML = splitContent;
        if (clipBoardElem.textContent.trim() !== '') {
            this.saveSelection.restore();
            this.parent.trigger(events.afterPasteCleanup, { value: clipBoardElem.innerHTML, filesData: null }, (updatedArgs) => { value = updatedArgs.value; });
            clipBoardElem.innerHTML = value;
            this.parent.formatter.editorManager.execCommand('inserthtml', 'pasteCleanup', args, (returnArgs) => {
                extend(args, { elements: returnArgs.elements, imageElements: returnArgs.imgElem }, true);
                this.parent.formatter.onSuccess(this.parent, args);
            }, clipBoardElem, null, null, this.parent.enterKey);
            this.pasteObj.removeTempClass();
        }
        else {
            this.saveSelection.restore();
            extend(args, { elements: [] }, true);
            this.parent.formatter.onSuccess(this.parent, args);
        }
    }
    /**
     * For internal use only - Get the module name.
     *
     * @returns {void}
     * @hidden
     */
    getModuleName() {
        return 'pasteCleanup';
    }
}
